import { Controller } from '@hotwired/stimulus';

/** Séparateur de milliers, identique à celui du `number_format` côté Twig. */
const THOUSANDS_SEPARATOR = ' ';

/**
 * Champ monétaire affiché avec un séparateur de milliers.
 *
 * Un `input[type=number]` refuse les séparateurs : le champ visible est donc en `type=text` et
 * n'est pas relié au LiveComponent. C'est l'input caché (cible `value`) qui porte le `data-model`
 * et transmet la valeur numérique brute — même principe que `form-slider`.
 */
export default class extends Controller<HTMLElement> {
  static targets = ['display', 'value'];

  declare readonly displayTarget: HTMLInputElement;
  declare readonly valueTarget: HTMLInputElement;

  connect (): void {
    this.displayTarget.value = this.format(this.parse(this.displayTarget.value));
  }

  /** Reformate à chaque frappe, en gardant le curseur au même rang parmi les chiffres. */
  reformat (): void {
    const input = this.displayTarget;
    const caret = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = this.countDigits(input.value.slice(0, caret));

    input.value = this.format(this.parse(input.value));

    const position = this.offsetAfterDigits(input.value, digitsBeforeCaret);
    input.setSelectionRange(position, position);
  }

  /** Propage la valeur brute au LiveComponent. Un champ vidé retombe sur le minimum. */
  commit (): void {
    const value = this.clamp(this.parse(this.displayTarget.value) ?? this.min);

    this.displayTarget.value = this.format(value);
    this.valueTarget.value = String(value);
    this.valueTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Remplace les flèches haut/bas que fournissait le `type=number`. */
  step (event: KeyboardEvent): void {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return;
    }
    event.preventDefault();

    const step = Number(this.displayTarget.dataset.step) || 1;
    const current = this.parse(this.displayTarget.value) ?? this.min;

    this.displayTarget.value = this.format(current + (event.key === 'ArrowUp' ? step : -step));
    this.commit();
  }

  private get min (): number {
    return Number(this.displayTarget.dataset.min ?? 0);
  }

  private get max (): number {
    return Number(this.displayTarget.dataset.max ?? Number.MAX_SAFE_INTEGER);
  }

  private clamp (value: number): number {
    return Math.min(Math.max(value, this.min), this.max);
  }

  /** `null` tant que l'utilisateur n'a saisi aucun chiffre, pour ne pas forcer un 0 pendant la frappe. */
  private parse (value: string): number | null {
    const digits = value.replace(/\D/g, '');

    return digits === '' ? null : Number(digits);
  }

  private format (value: number | null): string {
    if (null === value) {
      return '';
    }

    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, THOUSANDS_SEPARATOR);
  }

  private countDigits (value: string): number {
    return value.replace(/\D/g, '').length;
  }

  /** Position juste après le n-ième chiffre de la chaîne formatée. */
  private offsetAfterDigits (value: string, count: number): number {
    if (0 === count) {
      return 0;
    }

    let seen = 0;
    for (let i = 0; i < value.length; i++) {
      if (/\d/.test(value[i])) {
        seen++;
        if (seen === count) {
          return i + 1;
        }
      }
    }

    return value.length;
  }
}
